<template>
    <!-- Payroll Info ----------------------------------------------------------------------------------------------->
    <div class="w-[100%] pt-2 pb-4 bg-white flex justify-between items-center" style="z-index:100">
        <div class="flex flex-col">
            <h4 class="font-heading-sm-semibold">Payroll Info</h4>
            <p
                v-if="isEditingMode"
                class="font-body-sm-regular text-accent-red-3"
            >
                You are in the editing mode now. Please click the save button.
            </p>
        </div>
        <div v-if="$appUtil.checkPermission('SALARY_INFO_EDIT')">
            <div v-if="!isEditingMode">
                <DesignButton
                    class="w-[59px]"
                    btnText="Edit"
                    btnSize="SM"
                    btnType="PRIMARY"
                    btnVariant="BORDER"
                    :btnDisabled="false"
                    @click-event="isEditingMode = true"
                >
                    <template #front-icon>
                        <EditIcon :width="16" :height="16" color="text-primary-3" weight="REGULAR" />
                    </template>
                </DesignButton>
            </div>
            <div v-else class="flex items-center gap-1">
                <DesignButton
                    class="w-[64px]"
                    btnText="Cancel"
                    btnSize="SM"
                    btnType="SECONDARY"
                    btnVariant="BORDER"
                    :btnDisabled="false"
                    @click-event="setPayrollInfo(employeeData); isEditingMode = false"
                />
                <DesignButton
                    class="w-[64px]"
                    btnText="Save"
                    btnSize="SM"
                    btnType="PRIMARY"
                    btnVariant="FILL"
                    :btnDisabled="false"
                    @click-event="updatePayrollInfo"
                />
            </div>
        </div>
    </div>

    <div class="flex flex-col gap-4">
        <div class="flex justify-between items-center gap-2">
            <div class="c_label w-[8rem] horizon">TIN</div>
            <DesignInput 
                v-model="tinDisplay"
                :placeholder="isEditingMode ? 'Enter TIN' : '-'"
                :disabled="!isEditingMode"
                class="flex-1"
                :maxlength="17" />
        </div>
        <div class="flex justify-between items-center gap-2">
            <div class="c_label w-[8rem] horizon">SSS No.</div>
            <DesignInput 
                v-model="sssDisplay"
                :placeholder="isEditingMode ? 'Enter SSS No.' : '-'"
                :disabled="!isEditingMode"
                class="flex-1"
                :maxlength="12" />
        </div>
        <div class="flex justify-between items-center gap-2">
            <div class="c_label w-[8rem] horizon">PhilHealth No.</div>
            <DesignInput
                v-model="philhealthDisplay"
                :placeholder="isEditingMode ? 'Enter PhilHealth No.' : '-'"
                :disabled="!isEditingMode"
                class="flex-1"
                :maxlength="14" />
        </div>
        <div class="flex justify-between items-center gap-2">
            <div class="c_label w-[8rem] horizon">PAG-IBIG No. / HDMF</div>
            <DesignInput
                v-model="pagIbigDisplay"
                :placeholder="isEditingMode ? 'Enter PAG-IBIG No. / HDMF' : '-'"
                class="flex-1"
                :disabled="!isEditingMode"
                :maxlength="14" />
        </div>
        <div class="flex justify-between items-center gap-2">
            <div class="c_label w-[8rem] horizon">RDO Code</div>
            <DesignInput
                v-model="payrollInfo.rdoCode"
                :placeholder="isEditingMode ? 'Enter RDO Code' : '-'"
                class="flex-1"
                :disabled="!isEditingMode"
                :maxlength="3" />
        </div>
        <div class="flex justify-between items-center gap-2">
            <div class="c_label w-[8rem] horizon">Bank Name</div>
            <DesignInput
                v-model="payrollInfo.bankName"
                :placeholder="isEditingMode ? 'Enter Bank Name' : '-'"
                class="flex-1"
                :disabled="!isEditingMode" />
        </div>
        <div class="flex justify-between items-center gap-2">
            <div class="c_label w-[8rem] horizon">Bank Account Number</div>
            <DesignInput
                v-model="payrollInfo.bankAccountNumber"
                :placeholder="isEditingMode ? 'Enter Bank Account Number' : '-'"
                class="flex-1"
                :disabled="!isEditingMode" />
        </div>
    </div>

    <!-- Salary Details ----------------------------------------------------------------------------------------------->
    <div class="w-[100%] pt-2 pb-4 bg-white flex justify-between items-center mt-10" style="z-index:100">
        <h4 class="font-heading-sm-semibold">Salary Details</h4>
        <div class="flex gap-2">
            <DesignButton
                class="w-[117px] !px-0"
                btnText="Salary History"
                btnSize="SM"
                btnType="SECONDARY"
                btnVariant="BORDER"
                :btnDisabled="false"
                @click-event="clickHistory"
            >
                <template #front-icon>
                    <HistoryIcon :width="16" :height="16" color="text-content-basic-1" />
                </template>
            </DesignButton>
            <DesignButton
                v-if="$appUtil.checkPermission('SALARY_INFO_EDIT')"
                class="w-[118px] !px-0"
                :btnText="salaryInfo == null? 'Add Salary':'Update Salary'"
                btnSize="SM"
                btnType="PRIMARY"
                btnVariant="BORDER"
                :btnDisabled="false"
                @click-event="addOrEditButton"
            >
                <template #front-icon>
                    <EditIcon :width="16" :height="16" color="text-primary-3" weight="REGULAR" />
                </template>
            </DesignButton>
        </div>
    </div>

    <div class="flex flex-col gap-4">
        <template v-if="salaryInfo">
            <div class="flex flex-col gap-4">
                <div class="flex justify-between items-center gap-2">
                    <div class="c_label w-[8rem] horizon">PIC</div>
                    <DesignInput
                        :modelValue="salaryInfo.pic"
                        class="flex-1"
                        :disabled="true"
                        :readonly="true"
                        placeholder="-"
                    />
                </div>

                <div class="flex justify-between items-center gap-2">
                    <div class="c_label w-[8rem] horizon">Join Date</div>
                    <DesignInput
                        :modelValue="salaryJoinDateDisplay"
                        class="flex-1"
                        :disabled="true"
                        :readonly="true"
                        placeholder="-"
                    />
                </div>

                <div class="flex justify-between items-center gap-2">
                    <div class="c_label w-[8rem] horizon">Effective Date</div>
                    <DesignInput
                        :modelValue="salaryEffectiveDateDisplay"
                        class="flex-1"
                        :disabled="true"
                        :readonly="true"
                        placeholder="-"
                    />
                </div>

                <div class="flex justify-between items-center gap-2">
                    <div class="c_label w-[8rem] horizon">Employment Type</div>
                    <DesignInput
                        :modelValue="'Regular'"
                        class="flex-1"
                        :disabled="true"
                        :readonly="true"
                        placeholder="-"
                    />
                </div>

                <div class="flex justify-between items-start gap-2">
                    <div class="c_label w-[8rem] horizon">Amount</div>
                    <div class="flex-1 flex flex-col gap-2">
                        <DesignDropdownSelect
                            :modelValue="salaryInfo.salaryAmountType"
                            :options="salaryAmountTypeOptions"
                            :disabled="true"
                            :useDefaultMsg="false"
                            width="w-full"
                            size="LG"
                            placeholder="Salary Type"
                        />
                        <DesignAmountInput
                            :intPartLimit="9"
                            :modelValue="salaryInfo.salaryAmount"
                            class="flex-1"
                            :readonly="true"
                            :disabled="true"
                            placeholder="AMOUNT"
                        />
                    </div>
                </div>

                <div class="flex justify-between items-start gap-2">
                    <div class="c_label w-[8rem] horizon">Tax Code</div>
                    <div class="flex-1 flex flex-col gap-2">
                        <DesignDropdownSelect
                            :modelValue="salaryInfo.taxCode"
                            :options="taxCodeOptions"
                            :disabled="true"
                            :useDefaultMsg="false"
                            width="w-full"
                            size="LG"
                            placeholder="Tax Code"
                        />
                        <DesignFileUpload
                            :modelValue="salaryInfo.taxFilePath"
                            :fileName="salaryInfo.taxFileName? salaryInfo.taxFileName: ''"
                            size="LG"
                            :disabled="true"
                            :readonly="true"
                            width="w-full"
                        />
                    </div>
                </div>

                <div class="flex justify-between items-start gap-2">
                    <div class="c_label w-[8rem] horizon">PERA Info</div>
                    <div class="flex-1 flex flex-col gap-2">
                        <DesignInput
                            :modelValue="salaryInfo.peraYear"
                            class="flex-1"
                            placeholder="YEAR"
                            :readonly="true"
                            :disabled="true"
                        />
                        <DesignDropdownSelect
                            :modelValue="salaryInfo.peraType"
                            :options="peraTypeOptions"
                            :disabled="true"
                            :useDefaultMsg="false"
                            placeholder="PERA Type"
                            width="w-full"
                            size="LG"
                        />
                        <DesignAmountInput
                            :modelValue="salaryInfo.peraAmount"
                            class="flex-1"
                            placeholder="AMOUNT"
                            :readonly="true"
                            :disabled="true"
                        />
                        <DesignFileUpload
                            v-if="salaryInfo.peraCertificateFilePath"
                            :modelValue="salaryInfo.peraCertificateFilePath"
                            :fileName="salaryInfo.peraCertificateFileName? salaryInfo.peraCertificateFileName: ''"
                            size="LG"
                            :disabled="true"
                            :readonly="true"
                            width="w-full"
                        />
                    </div>
                </div>

                <div class="flex justify-between items-start gap-2">
                    <div class="c_label w-[8rem] horizon">Other Details</div>
                    <div class="flex-1">
                        <textarea
                            class="w-full h-[6rem] resize-none border border-border-basic-2 rounded-2 bg-background-basic-3 px-4 py-3 font-body-sm-regular text-content-basic-1"
                            :value="salaryInfo.otherDetails"
                            readonly
                            :disabled="true"
                            placeholder="-"
                        />
                    </div>
                </div>
            </div>
        </template>
        <div v-else class="flex flex-col justify-center items-center p-20">
            <img class="mb-4" src="@/assets/img/none.svg" alt=""/>
            <p class="text-sm text-[#85878B] font-medium">
                Please Register Payroll
            </p>
        </div>
    </div>

    <Teleport to="body">
        <AppPopup
            @button-event="addOrEditSaveButtonClick"
            v-model="addOrEditPopup"
            button-text="Save"
            max-width="640px"
            :name="salaryInfo == null ? 'Add Salary' : 'Update Salary Info'">
            <AddSalary
                v-model="addOrEditPopup"
                :salaryInfo="salaryInfo"
                :employeeData="employeeData"
                ref="addSalary"/>
        </AppPopup>

        <AppPopup v-model="showHistoryPopup" :name="'Salary History'" max-width="480px">
            <HistorySalary
                v-model="showHistoryPopup"
                :employeeSeq="employeeSeq"/>
        </AppPopup>

        <AppPopupWithCenteredButtons
            @button-event="updateSalary"
            v-model="salaryUpdateComfirmPopup"
            :backgroundWhite="false"
            :disabled="false"
            name="Update Salary"
            button-text="Update">
            <div class="mt-4 mb-6 text-center max-w-[644px] max-h-96 overflow-auto">
                <span class="ml-3 pr-5">
                    Are you sure you want to update the salary of this employee?
                </span>
            </div>
        </AppPopupWithCenteredButtons>

        <ConfirmPopup
            v-model="showSaveNewSalaryPopup"
            confirmDescription="The update has been successfully completed."
            :hasAfterConfirm="true"
            @after-confirm="closeSaveSalaryInfoModal"
            buttonText="OK"/>
    </Teleport>
</template>

<script>
import moment from 'moment';
import AddSalary from '@/pages/employees/employees/popups/PopupAddSalary.vue';
import HistorySalary from '@/pages/employees/employees/popups/PopupSalaryInfoHistory.vue';
import ConfirmPopup from '@/components/ConfirmPopup.vue';
import AppPopupWithCenteredButtons from '@/components/AppPopupWithCenteredButtons.vue';
import DesignInput from '@/components/design-system/design-input.vue';
import DesignAmountInput from '@/components/design-system/design-amount-input.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import DesignDropdownSelect from '@/components/design-system/design-dropdown-select.vue';
import DesignFileUpload from '@/components/design-system/design-file-upload.vue';
import EditIcon from '@/components/design-system/icon/edit.vue';
import HistoryIcon from '@/components/design-system/icon/history.vue';
import {
    salaryAmountTypeOptions,
    taxCodeOptions,
    peraTypeOptions,
} from '@/pages/employees/employees/popups/salaryOptionData.js';

export default {
    components: {
        AddSalary,
        HistorySalary,
        ConfirmPopup,
        AppPopupWithCenteredButtons,
        DesignInput,
        DesignAmountInput,
        DesignButton,
        DesignDropdownSelect,
        DesignFileUpload,
        EditIcon,
        HistoryIcon,
    },
    props: {
        employeeData: Object,
    },
    emits: ['refresh-employee-data'],
    data() {
        return {
            employeeSeq: null,
            salaryInfo: null,
            salaryInfoList: null,
            addOrEditPopup: false,
            salaryUpdateComfirmPopup: false,
            isEditingMode: false,
            showHistoryPopup: false,
            payrollInfo: {
                tinNo: '',
                bankName: '',
                bankAccountNumber: '',
                pagIbig: '',
                socialSecuritySystem: '',
                philhealth: '',
                rdoCode: '',
            },
            salaryType: {
                ANNUALLY: 'Annually',
                MONTHLY: 'Monthly',
                WEEKLY: 'Weekly',
                DAILY: 'Daily',
                HOURLY: 'Hourly',
            },
            salaryAmountTypeOptions,
            taxCodeOptions,
            peraTypeOptions,
            showSaveNewSalaryPopup: false,
        };
    },
    watch: {
        employeeData: {
            handler(newVal) {
                if (newVal) {
                    this.setPayrollInfo(newVal);
                }
            },
            deep: false,
        },
    },
    methods: {
        moment: moment,
        parseInt: parseInt,
        getEmployeeSalaryInfo() {
            const self = this;

            if (!self.employeeSeq) {
                return;
            }

            const url = self
                .$api('uri', 'get-employee-salary-info')
                .replace('{employeeSeq}', self.employeeSeq);

            self.$axios
                .get(url)
                .then((res) => {
                    self.salaryInfo = res.data.data.employeeSalaryInfo;
                    self.salaryInfoList = res.data.data.employeeSalaryInfoList;
                })
                .catch((err) => {
                    console.log(err);
                });
        },
        addOrEditSaveButtonClick() {
            const salaryData = this.$refs.addSalary.getSalaryData();
            if (!this.validationSalary(salaryData)) {
                return;
            }

            this.salaryUpdateComfirmPopup = true;
        },
        updateSalary() {
            const salaryData = this.$refs.addSalary.getSalaryData();
            const employeeSeq = this.employeeSeq;
            const requestItem = {...salaryData};

            const url = this.$api('uri', 'post-employee-salary-info').replace(
                '{employeeSeq}',
                employeeSeq
            );
            this.$axios
                .post(url, requestItem)
                .then((res) => {
                    this.salaryUpdateComfirmPopup = false;
                    this.addOrEditPopup = false;
                    this.getEmployeeSalaryInfo();
                    this.showSaveNewSalaryPopup = true;
                })
                .catch((err) => {
                    console.log(err.response);
                });
        },
        closeSaveSalaryInfoModal() {
            this.showSaveNewSalaryPopup = false;
        },
        validationSalary(salaryData) {
            if (!salaryData) {
                return false;
            }

            let salaryAmountType = salaryData.salaryAmountType;
            let salaryAmount = salaryData.salaryAmount;
            let taxCode = salaryData.taxCode;
            let effectiveDate = salaryData.effectiveDate;
            let pic = salaryData.pic;

            const self = this;
            if (salaryAmountType == null || salaryAmountType === '') {
                alert('Please select a salary type.');
                return false;
            }

            if (salaryAmount == null || salaryAmount === '' || Number.isNaN(salaryAmount)) {
                alert('Please enter the Basic Salary Amount.');
                return false;
            }

            if (salaryAmount <= 0) {
                self.salaryInfo.errorMessage = 'Please enter an amount greater than zero.';
                return false;
            }
            self.salaryInfo.errorMessage = null

            if (taxCode == null || taxCode === '') {
                alert('Please select a tax code.');
                return false;
            }

            if (effectiveDate == null) {
                alert('Please select a effective Date.');
                return false;
            }

            if (effectiveDate < salaryData.minEffectiveDate) {
                alert('Please set the effective date last payroll cut off or join date');
                return false;
            }

            if (self.salaryInfoList != null && self.salaryInfoList.length > 0) {
                for (let salaryInformation of self.salaryInfoList) {
                    let tempEffectiveDate = moment(salaryInformation.effectiveDate).format(
                        'YYYYMMDD'
                    );
                    if (effectiveDate === tempEffectiveDate) {
                        alert('An effective date with the same date already exists.');
                        return false;
                    }
                }
            }

            if (pic == null || pic === '') {
                alert('Please input the name of PIC');
                return false;
            }

            // 비 필수값 확인
            let peraYear = salaryData.peraYear;
            let peraAmount = salaryData.peraAmount;
            if (peraYear != null) {
                if (Number.isNaN(peraYear)) {
                    alert('Please enter a number in pera year.');
                    return false;
                }

                if (peraYear < 0) {
                    alert('The Pera year must be positive.');
                    return false;
                }
            }

            if (peraAmount != null) {
                if (Number.isNaN(peraAmount)) {
                    alert('Please enter a number in pera amount.');
                    return false;
                }

                if (peraAmount < 0) {
                    alert('The Pera amount must be positive.');
                    return false;
                }
            }

            return true;
        },
        updatePayrollInfo() {
            const self = this;

            const employeeSeq = self.employeeSeq;
            const requestItem = {...self.payrollInfo};

            const url = self
                .$api('uri', 'put-employee-salary')
                .replace('{employeeSeq}', employeeSeq);
            self.$axios
                .put(url, requestItem)
                .then((res) => {
                    self.isEditingMode = false;
                    self.$emit('refresh-employee-data', self.employeeSeq);
                })
                .catch((err) => {
                    console.log(err.response);
                });
        },
        setPayrollInfo(employeeData) {
            const self = this;
            self.payrollInfo.tinNo = employeeData.tinNo;
            self.payrollInfo.socialSecuritySystem = employeeData.socialSecuritySystem;
            self.payrollInfo.philhealth = employeeData.philhealth;
            self.payrollInfo.pagIbig = employeeData.pagIbig;
            self.payrollInfo.rdoCode = employeeData.rdoCode;
            self.payrollInfo.bankName = employeeData.bankName;
            self.payrollInfo.bankAccountNumber = employeeData.bankAccountNumber;
        },
        addOrEditButton() {
            const self = this;
            self.addOrEditPopup = true;
        },
        clickHistory() {
            const self = this;
            self.showHistoryPopup = true;
        },
        getTinNumberFormat(input) {
            const inputValue = input?.replace(/\D/g, ''); // Remove non-digit characters
            let formattedValue = '';

            if (inputValue) {
                formattedValue = inputValue.match(new RegExp('.{1,3}', 'g')).join('-'); // xxx-xxx-xxx
                const regex = /^(\d{3})(\d{3})(\d{3})(\d{1,})$/;
                const match = regex.exec(inputValue);

                if (match) {
                    formattedValue = `${match[1]}-${match[2]}-${match[3]}-${match[4]}`; // xxx-xxx-xxxxx
                }
            }

            return formattedValue;
        },
        getSSSNumberFormat(input) {
            if (!input) {
                return;
            }

            const inputValue = input?.replace(/\D/g, ''); // Remove non-digit characters
            let formattedValue = '';

            if (inputValue) {
                //
                const patternIndexArr = [2, 7];
                let patternValueArr = [];

                let value = inputValue;
                for (const pIndex of patternIndexArr) {
                    if (value.length > pIndex) {
                        let cut = value.substr(0, pIndex);
                        value = value.substr(pIndex);
                        patternValueArr.push(cut);
                    } else {
                        break;
                    }
                }

                if (value) {
                    patternValueArr.push(value);
                }

                formattedValue = patternValueArr.join('-');
            }

            return formattedValue;
        },
        getPhilhealthFormat(input) {
            if (!input) {
                return;
            }

            const inputValue = input?.replace(/\D/g, ''); // Remove non-digit characters
            let formattedValue = '';

            if (inputValue) {
                //
                const patternIndexArr = [2, 9];
                let patternValueArr = [];

                let value = inputValue;
                for (const pIndex of patternIndexArr) {
                    if (value.length > pIndex) {
                        let cut = value.substr(0, pIndex);
                        value = value.substr(pIndex);
                        patternValueArr.push(cut);
                    } else {
                        break;
                    }
                }

                if (value) {
                    patternValueArr.push(value);
                }

                formattedValue = patternValueArr.join('-');
            }

            return formattedValue;
        },
        getPagIbigFormat(input) {
            if (!input) {
                return;
            }

            const inputValue = input?.replace(/\D/g, ''); // Remove non-digit characters
            let formattedValue = '';

            if (inputValue) {
                //
                const patternIndexArr = [4, 4];
                let patternValueArr = [];

                let value = inputValue;
                for (const pIndex of patternIndexArr) {
                    if (value.length > pIndex) {
                        let cut = value.substr(0, pIndex);
                        value = value.substr(pIndex);
                        patternValueArr.push(cut);
                    } else {
                        break;
                    }
                }

                if (value) {
                    patternValueArr.push(value);
                }

                formattedValue = patternValueArr.join('-');
            }

            return formattedValue;
        },
    },
    mounted() {
        const self = this;

        if (self.employeeData) {
            self.employeeSeq = self.employeeData.employeeSeq;
            self.getEmployeeSalaryInfo();
            self.setPayrollInfo(self.employeeData);
        }
    },
    computed: {
        tinDisplay: {
            get() {
                return this.getTinNumberFormat(this.payrollInfo?.tinNo || '');
            },
            set(val) {
                const raw = (val || '').replace(/\D/g, '');
                if (this.payrollInfo) {
                    this.payrollInfo.tinNo = raw;
                }
            },
        },
        sssDisplay: {
            get() {
                return this.getSSSNumberFormat(this.payrollInfo?.socialSecuritySystem || '');
            },
            set(val) {
                const raw = (val || '').replace(/\D/g, '');
                if (this.payrollInfo) {
                    this.payrollInfo.socialSecuritySystem = raw;
                }
            },
        },
        philhealthDisplay: {
            get() {
                return this.getPhilhealthFormat(this.payrollInfo?.philhealth || '');
            },
            set(val) {
                const raw = (val || '').replace(/\D/g, '');
                if (this.payrollInfo) {
                    this.payrollInfo.philhealth = raw;
                }
            },
        },
        pagIbigDisplay: {
            get() {
                return this.getPagIbigFormat(this.payrollInfo?.pagIbig || '');
            },
            set(val) {
                const raw = (val || '').replace(/\D/g, '');
                if (this.payrollInfo) {
                    this.payrollInfo.pagIbig = raw;
                }
            },
        },
        salaryJoinDateDisplay() {
            if (!this.employeeData || !this.employeeData.joinDate) {
                return '-';
            }
            return moment(parseInt(this.employeeData.joinDate, 10), 'YYYYMMDD').format(
                'MM/DD/YYYY'
            );
        },
        salaryEffectiveDateDisplay() {
            if (!this.salaryInfo || !this.salaryInfo.effectiveDate) {
                return '-';
            }
            return moment(this.salaryInfo.effectiveDate).format('MM/DD/YYYY');
        },
    },
};
</script>
